export interface OfflineState {
  version: string
  completed: number
  total: number
  savedBytes: number
  totalBytes: number
  ready: boolean
  downloading: boolean
}
export interface OfflineResult { state: OfflineState; failures?: string[] }
interface OfflineMessage { type: 'STATUS' | 'PROGRESS' | 'DONE' | 'ERROR'; state?: OfflineState; failures?: string[]; message?: string }

let registrationPromise: Promise<ServiceWorkerRegistration | null> | undefined

export function supportsOfflineInstall(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const capacitor = (window as Window & { Capacitor?: { isNativePlatform?(): boolean } }).Capacitor
  const location = window.location
  const secure = location.protocol === 'https:' || (location.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname))
  return secure && 'serviceWorker' in navigator && !capacitor?.isNativePlatform?.()
}

export function registerOfflineSupport(): Promise<ServiceWorkerRegistration | null> {
  if (!supportsOfflineInstall()) return Promise.resolve(null)
  // The initial install downloads only the small shell, never the audio library.
  registrationPromise ??= navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).then(async () => {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('离线组件暂未准备好，请保持联网后重试。')), 20000)),
    ])
  }).catch((error: unknown) => { registrationPromise = undefined; throw error })
  return registrationPromise
}

async function communicate(type: 'OFFLINE_STATUS' | 'OFFLINE_DOWNLOAD', onProgress?: (state: OfflineState) => void): Promise<OfflineResult> {
  const registration = await registerOfflineSupport()
  if (!registration?.active) throw new Error('请在 iPhone Safari 中通过 HTTPS 打开小花园。')
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel()
    let timer: ReturnType<typeof setTimeout>
    const close = () => { clearTimeout(timer); channel.port1.close() }
    const touch = () => {
      clearTimeout(timer)
      timer = setTimeout(() => { close(); reject(new Error('下载中断。已经保存的文件会保留，保持页面打开后点重试。')) }, 90000)
    }
    channel.port1.onmessage = (event: MessageEvent<OfflineMessage>) => {
      touch()
      if (event.data.type === 'ERROR') { close(); reject(new Error('下载未完成，可能网络中断或存储不足。已保存的文件会保留，请重试。')); return }
      if (!event.data.state) return
      onProgress?.(event.data.state)
      if (event.data.type === 'STATUS' || event.data.type === 'DONE') {
        close()
        resolve({ state: event.data.state, failures: event.data.failures })
      }
    }
    touch()
    registration.active!.postMessage({ type }, [channel.port2])
  })
}

export async function getOfflineState(): Promise<OfflineState> { return (await communicate('OFFLINE_STATUS')).state }

export async function downloadOfflinePack(onProgress: (state: OfflineState) => void): Promise<OfflineResult> {
  // Request persistent storage only following an explicit parent download action.
  try { await navigator.storage?.persist?.() } catch { /* Safari may not offer persistent-storage grants. */ }
  return communicate('OFFLINE_DOWNLOAD', onProgress)
}

export async function hasWaitingOfflineUpdate(): Promise<boolean> {
  return Boolean((await registerOfflineSupport())?.waiting)
}
